import { Exercise } from "@domain/exercise/exercise";
import { SESSION_DROPPABLE_HORIZONTAL_PADDING } from "@ui/pages/program-builder/day-table/day-exercise-placeholder";
import { ExerciseDragAndDropContext } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import React, { CSSProperties, useContext, useEffect, useState } from "react";
import {
	BeforeCapture,
	Draggable,
	DraggableProvided,
	DraggableProvidedDraggableProps,
	DraggableStateSnapshot,
} from "@hello-pangea/dnd";

type DraggableCardStyle = "row" | "card";

interface DraggableCardProps {
	renderCard: ({
		cardStyle,
		overriddenUiStyle,
		provided,
		snapshot,
	}: {
		cardStyle: CSSProperties;
		overriddenUiStyle?: DraggableCardStyle;
		provided: DraggableProvided;
		snapshot: DraggableStateSnapshot;
	}) => JSX.Element;
	exercise: Exercise;
}

export const DraggableCard: React.FC<DraggableCardProps & { index: number; copy?: boolean }> = ({
	exercise,
	renderCard,
	index,
	copy,
}) => {
	const dragAndDropContext = useContext(ExerciseDragAndDropContext);
	const [overriddenUiStyle, setOverriddenUiStyle] = useState<{ uiStyle: DraggableCardStyle; style: CSSProperties }>();
	const getCardStyle = (dragProps: DraggableProvidedDraggableProps, isDragging: boolean) => ({
		...dragProps.style,
		...(copy
			? {
					//copy style to duplicate item
					transform: isDragging ? dragProps.style?.transform : "translate(0px, 0px)",
				}
			: {}),
	});

	useEffect(() => {
		const handleDragEnd = () => {
			setOverriddenUiStyle(undefined);
		};

		const handleBeforeCapture = (before: BeforeCapture) => {
			if (before.draggableId === exercise.draggableId) {
				const widthItem = document.getElementById("droppable_1")?.getBoundingClientRect().width;
				setOverriddenUiStyle({
					uiStyle: "card",
					style: {
						width: widthItem ? widthItem - 2 * SESSION_DROPPABLE_HORIZONTAL_PADDING : undefined,
						alignSelf: "center",
					},
				});
			}
		};
		dragAndDropContext?.onDragEnd.add(handleDragEnd);
		dragAndDropContext?.onBeforeCapture.add(handleBeforeCapture);
		return () => {
			dragAndDropContext?.onDragEnd.remove(handleDragEnd);
			dragAndDropContext?.onBeforeCapture.remove(handleBeforeCapture);
		};
	}, [dragAndDropContext?.onBeforeCapture, dragAndDropContext?.onDragEnd, exercise.draggableId]);

	return (
		<Draggable key={exercise.draggableId} draggableId={exercise.draggableId} index={index}>
			{(provided, snapshot) =>
				renderCard({
					cardStyle: {
						...getCardStyle(provided.draggableProps, snapshot.isDragging),
						...(overriddenUiStyle ? overriddenUiStyle.style : {}),
					},
					overriddenUiStyle: overriddenUiStyle?.uiStyle,
					provided,
					snapshot,
				})
			}
		</Draggable>
	);
};

